import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, MapPin, Loader, AlertTriangle, CheckCircle } from 'lucide-react';
import OTPVerification from './OTPVerification';
import SignUpSuccessModal from '../SignUpSuccessModal/SignUpSuccessModal';
import { trackAuth, trackError } from '../Utils/analytics';
import { getCurrentLocationDetails } from '../Utils/location';
import '../Auth/Auth.css';

const SignUp: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [countryName, setCountryName] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [locationStatus, setLocationStatus] = useState<'detecting' | 'success' | 'denied' | 'error' | 'initial'>('initial');
  const [locationError, setLocationError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();

  // Automatically detect location when component mounts
  React.useEffect(() => {
    detectUserLocation();
  }, []);

  const detectUserLocation = async () => {
    setLocationStatus('detecting');
    setLocationError('');

    try {
      const result = await getCurrentLocationDetails();
      
      if ('type' in result) {
        // Handle different error types
        if (result.type === 'permission_denied') {
          setLocationStatus('denied');
          setLocationError('Location access is required to register. Please enable location permissions and refresh the page.');
        } else {
          setLocationStatus('error');
          setLocationError(result.message);
        }
      } else {
        // Success - update country fields
        setCountryName(result.country_name);
        setCountryCode(result.country_code);
        setLocationStatus('success');
        
        console.log('Location detected successfully:', {
          country: result.country_name,
          code: result.country_code,
          address: result.full_address
        });
      }
    } catch (error) {
      console.error('Location detection failed:', error);
      setLocationStatus('error');
      setLocationError('Failed to detect location. Please refresh the page and allow location access.');
    }
  };

  const handleRetryLocation = () => {
    detectUserLocation();
  };

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password validation
  const validatePassword = (password: string): string[] => {
    const errors = [];
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/(?=.*\d)/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    return errors;
  };

  // Live validation for confirm password
  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    
    if (value && password && value !== password) {
      setErrors(prev => ({
        ...prev,
        confirmPassword: 'Passwords do not match'
      }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.confirmPassword;
        return newErrors;
      });
    }
  };

  // Validate all fields
  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    // Email validation
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Country validation
    if (!countryName.trim() || !countryCode.trim()) {
      newErrors.country = 'Country is required';
    }

    // Password validation
    if (!password) {
      newErrors.password = 'Password is required';
    } else {
      const passwordErrors = validatePassword(password);
      if (passwordErrors.length > 0) {
        newErrors.password = passwordErrors[0]; // Show first error
      }
    }

    // Confirm password validation
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password && confirmPassword !== password) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if location detection is still in progress
    if (locationStatus === 'detecting') {
      setErrors({ general: 'Please wait for location detection to complete.' });
      return;
    }
    
    // Check if location access was denied
    if (locationStatus === 'denied') {
      setErrors({ general: 'Location access is required for registration. Please enable location permissions and try again.' });
      return;
    }
    
    // Check if location detection failed
    if (locationStatus === 'error' || !countryCode) {
      setErrors({ general: 'Location detection failed. Please refresh the page and allow location access to continue.' });
      return;
    }
    
    if (!validateForm()) {
      trackError('signup_validation_failed', 'signup');
      return;
    }

    setIsLoading(true);
    setErrors({}); // Clear any existing errors
    
    try {
      const response = await fetch('https://api.homecookt.com/api/v1/users/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          country: countryCode, // Always send country code to database
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Success - show OTP verification
        setShowOTPVerification(true);
        trackAuth('signup');
      } else {
        // Log the full error response for debugging
        console.error('Backend signup error response:', data);
        console.error('Response status:', response.status);
        
        // Handle different error scenarios with specific messages
        if (response.status === 409) {
          setErrors({
            general: 'An account with this email already exists. Please log in instead or use a different email address.'
          });
          trackError('signup_email_exists', 'signup');
        } else if (response.status === 400) {
          // Handle validation errors from backend
          // Check for existing account indicators first (most common case)
          const messageText = (data.message || '').toLowerCase();
          const detailText = (data.detail || '').toLowerCase();
          const errorText = (data.error || '').toLowerCase();
          const allErrorText = `${messageText} ${detailText} ${errorText}`;
          
          if (allErrorText.includes('already exists') || 
              allErrorText.includes('already registered') || 
              allErrorText.includes('email taken') ||
              allErrorText.includes('user exists') ||
              allErrorText.includes('duplicate') ||
              allErrorText.includes('conflict')) {
            setErrors({
              general: 'This email is already registered. Please log in instead or use a different email address.'
            });
            trackError('signup_email_already_exists', 'signup');
          } else if (data.message && data.message.toLowerCase().includes('email')) {
            if (data.message.toLowerCase().includes('already exists') || data.message.toLowerCase().includes('taken')) {
              setErrors({
                general: 'This email is already registered. Please log in or use a different email address.'
              });
              trackError('signup_email_taken', 'signup');
            } else if (data.message.toLowerCase().includes('invalid') || data.message.toLowerCase().includes('format')) {
              setErrors({
                email: 'Please enter a valid email address.'
              });
              trackError('signup_invalid_email', 'signup');
            } else {
              setErrors({
                email: data.message || 'Email validation failed.'
              });
              trackError('signup_email_validation', 'signup');
            }
          } else if (data.message && data.message.toLowerCase().includes('password')) {
            if (data.message.toLowerCase().includes('weak') || data.message.toLowerCase().includes('strength')) {
              setErrors({
                password: 'Password is too weak. Please use a stronger password with at least 8 characters, including uppercase, lowercase, and numbers.'
              });
              trackError('signup_weak_password', 'signup');
            } else if (data.message.toLowerCase().includes('length') || data.message.toLowerCase().includes('short')) {
              setErrors({
                password: 'Password must be at least 8 characters long.'
              });
              trackError('signup_short_password', 'signup');
            } else {
              setErrors({
                password: data.message || 'Password validation failed.'
              });
              trackError('signup_password_validation', 'signup');
            }
          } else if (data.message && data.message.toLowerCase().includes('country')) {
            setErrors({
              country: 'Please enter a valid country name.'
            });
            trackError('signup_invalid_country', 'signup');
          } else if (data.message && data.message.toLowerCase().includes('required')) {
            setErrors({
              general: 'Please fill in all required fields correctly.'
            });
            trackError('signup_missing_fields', 'signup');
          } else {
            // Fallback: if we can't identify the specific issue, show the backend message or a generic one
            setErrors({
              general: data.message || data.detail || data.error || 'Please check your information and try again.'
            });
            trackError('signup_validation_error', 'signup');
          }
        } else if (response.status === 422) {
          setErrors({
            general: 'The information provided is invalid. Please check your details and try again.'
          });
          trackError('signup_invalid_data', 'signup');
        } else if (response.status === 429) {
          setErrors({
            general: 'Too many signup attempts. Please wait a few minutes before trying again.'
          });
          trackError('signup_rate_limited', 'signup');
        } else if (response.status >= 500) {
          setErrors({
            general: 'Server error. Please try again later or contact support if the problem persists.'
          });
          trackError('signup_server_error', 'signup');
        } else {
          // Fallback for any other error codes
          setErrors({
            general: data.message || 'Registration failed. Please try again.'
          });
          trackError('signup_unknown_error', 'signup');
        }
      }
    } catch (error) {
      console.error('Signup error:', error);
      setErrors({
        general: 'Network error. Please check your internet connection and try again.'
      });
      trackError('signup_network_error', 'signup');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPVerified = () => {
    setShowOTPVerification(false);
    setShowSuccessModal(true);
  };

  const handleBackToSignup = () => {
    setShowOTPVerification(false);
  };

  const handleSuccessModalContinue = () => {
    setShowSuccessModal(false);
    navigate('/login');
  };

  if (showSuccessModal) {
    return (
      <SignUpSuccessModal 
        email={email}
        onContinue={handleSuccessModalContinue}
      />
    );
  }

  if (showOTPVerification) {
    return (
      <OTPVerification 
        email={email}
        onVerified={handleOTPVerified}
        onBack={handleBackToSignup}
      />
    );
  }

  // Show location permission request screen
  if (locationStatus === 'detecting') {
    return (
      <div className="auth-container location-detecting-container">
        <div className="location-detecting-content">
          <div className="location-icon">
            <Loader size={60} className="location-spinner" />
          </div>
          <h2 className="location-title">Detecting Your Location</h2>
          <p className="location-description">
            We're detecting your location to automatically fill your country information. 
            Please allow location access when prompted by your browser.
          </p>
          <div className="location-steps">
            <div className="location-step">
              <span className="step-number">1</span>
              <span>Click "Allow" when browser asks for location permission</span>
            </div>
            <div className="location-step">
              <span className="step-number">2</span>
              <span>We'll automatically detect your country</span>
            </div>
            <div className="location-step">
              <span className="step-number">3</span>
              <span>Complete your registration</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show location denied screen
  if (locationStatus === 'denied') {
    return (
      <div className="auth-container location-denied-container">
        <div className="location-denied-content">
          <div className="location-denied-icon">
            <AlertTriangle size={60} />
          </div>
          <h2 className="location-denied-title">Location Access Required</h2>
          <p className="location-denied-description">
            Location access is required to register on HomeCookt. We need to detect your country 
            to provide you with relevant local content and comply with regional regulations.
          </p>
          <div className="location-denied-instructions">
            <h3>How to enable location access:</h3>
            <ol>
              <li>Click the location icon in your browser's address bar</li>
              <li>Select "Allow" for location permissions</li>
              <li>Refresh this page and try again</li>
            </ol>
          </div>
          <div className="location-denied-actions">
            <button onClick={handleRetryLocation} className="retry-location-btn">
              <MapPin size={20} />
              Try Again
            </button>
            <Link to="/" className="go-home-btn">
              Go to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Show location error screen
  if (locationStatus === 'error') {
    return (
      <div className="auth-container location-error-container">
        <div className="location-error-content">
          <div className="location-error-icon">
            <AlertTriangle size={60} />
          </div>
          <h2 className="location-error-title">Location Detection Failed</h2>
          <p className="location-error-description">
            {locationError || 'We couldn\'t detect your location. This might be due to browser settings or network issues.'}
          </p>
          <div className="location-error-actions">
            <button onClick={handleRetryLocation} className="retry-location-btn">
              <MapPin size={20} />
              Try Again
            </button>
            <Link to="/" className="go-home-btn">
              Go to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <h2 className="auth-title">Create Account</h2>
      <form className="auth-form" onSubmit={handleSignUp}>
        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={errors.email ? 'error' : ''}
            placeholder="Enter your email address"
            required
          />
          {errors.email && <div className="error-message">{errors.email}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="country">Country (Auto-detected) *</label>
          <div className="country-display-container">
            <div className="country-display">
              <div className="country-info">
                <CheckCircle size={20} className="country-success-icon" />
                <div className="country-details">
                  <span className="country-name">{countryName}</span>
                  <span className="country-code">({countryCode})</span>
                </div>
              </div>
              <button
                type="button"
                onClick={handleRetryLocation}
                className="retry-location-small-btn"
                title="Refresh location"
              >
                <MapPin size={16} />
              </button>
            </div>
          </div>
          {errors.country && <div className="error-message">{errors.country}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="password">Password *</label>
          <div className="password-input-container">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={errors.password ? 'error' : ''}
              placeholder="Create a strong password"
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.password && <div className="error-message">{errors.password}</div>}
          <div className="password-requirements">
            <small>Password must contain at least 8 characters, including uppercase, lowercase, and a number</small>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password *</label>
          <div className="password-input-container">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => handleConfirmPasswordChange(e.target.value)}
              className={errors.confirmPassword ? 'error' : ''}
              placeholder="Confirm your password"
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
        </div>

        {errors.general && <div className="error-message general-error">{errors.general}</div>}

        <button 
          type="submit" 
          className="auth-button"
          disabled={isLoading || Object.keys(errors).length > 0}
        >
          {isLoading ? 'Creating Account...' : 'Sign Up'}
        </button>
      </form>

      <div className="auth-switch">
        Already have an account?
        <Link to="/login">Log In</Link>
      </div>
    </div>
  );
};

export default SignUp;